import { useState, useEffect } from 'react'
import { Image, Download, Share2, Plus, FileText, Sparkles, Clock, HardDrive, BookOpen } from 'lucide-react'
import { imageLabService, type ImageLog, type GenerationRequest } from '../services/imageLabService'
import { defaultContexts, defaultTemplates, type DefaultContext } from '../data/imageLabDefaults'

interface PromptTemplate {
  id: string
  name: string
  diagramType: string
  visualStyle: string
  colorScheme: string
  customInstructions: string
}

interface GenerationResult {
  id: string
  imageUrl: string
  timestamp: string
  responseTime: number
  fileSize: number
  model: string
  rating?: 'A' | 'B' | 'C' | 'F'
  prompt: string
}

export default function ImageLab() {
  // State for logs and templates
  const [logs, setLogs] = useState<ImageLog[]>([])
  const [selectedLog, setSelectedLog] = useState<ImageLog | null>(null)
  const [templates, setTemplates] = useState<PromptTemplate[]>([])

  // State for context editing
  const [languagePair, setLanguagePair] = useState('')
  const [codeElement, setCodeElement] = useState('')
  const [surface, setSurface] = useState('')
  const [concept, setConcept] = useState('')
  const [paradigm, setParadigm] = useState('')

  // State for prompt builder
  const [diagramType, setDiagramType] = useState('control flow')
  const [visualStyle, setVisualStyle] = useState('clean')
  const [colorScheme, setColorScheme] = useState('language-coded')
  const [customInstructions, setCustomInstructions] = useState('')

  // State for results
  const [generationHistory, setGenerationHistory] = useState<GenerationResult[]>([])
  const [currentResult, setCurrentResult] = useState<GenerationResult | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [selectedQuality, setSelectedQuality] = useState({
    readability: false,
    visualHierarchy: false,
    colorCoding: false,
    layout: false,
    completeness: false,
    selfExplanatory: false
  })

  // Load logs on mount
  useEffect(() => {
    loadLogs()
    loadTemplates()
  }, [])

  const loadLogs = async () => {
    try {
      const data = await imageLabService.getLogs()
      setLogs(data)
    } catch (error) {
      console.error('Failed to load logs:', error)
    }
  }

  const loadTemplates = () => {
    const saved = localStorage.getItem('imagelab_templates')
    if (saved) {
      const userTemplates = JSON.parse(saved)
      // Merge default templates with user templates
      setTemplates([...defaultTemplates.map((t, i) => ({ ...t, id: `default-${i}` })), ...userTemplates])
    } else {
      // Load only default templates
      setTemplates(defaultTemplates.map((t, i) => ({ ...t, id: `default-${i}` })))
    }
  }

  const handleLoadLog = (log: ImageLog) => {
    setSelectedLog(log)
    setLanguagePair(log.languagePair)
    setCodeElement(log.codeElement)
    setSurface(log.context.surface)
    setConcept(log.context.concept)
    setParadigm(log.context.paradigm)

    // Try to infer diagram type from prompt
    const prompt = log.prompt.toLowerCase()
    if (prompt.includes('control flow')) setDiagramType('control flow')
    else if (prompt.includes('memory model')) setDiagramType('memory model')
    else if (prompt.includes('syntax comparison')) setDiagramType('syntax comparison')
    else if (prompt.includes('data structure')) setDiagramType('data structure')
  }

  const handleLoadDefaultContext = (defaultCtx: DefaultContext) => {
    setLanguagePair(defaultCtx.languagePair)
    setCodeElement(defaultCtx.codeElement)
    setSurface(defaultCtx.surface)
    setConcept(defaultCtx.concept)
    setParadigm(defaultCtx.paradigm)
    setDiagramType(defaultCtx.suggestedDiagramType)
  }

  const buildPrompt = (): string => {
    return `Create a clear, educational diagram that visually explains the programming concept: "${concept.split(':')[0] || 'Concept'}"

**Context:**
- Learning Path: ${languagePair}
- Code Element: \`${codeElement}\`
- Syntax Comparison: ${surface}

**Semantic Meaning:**
${concept}

**Paradigm Context:**
${paradigm}

**Diagram Requirements:**
1. **Visual Clarity**: Use simple shapes, arrows, and labels
2. **Educational Focus**: Show how ${languagePair.split(' → ')[0] || 'source'} concepts map to ${languagePair.split(' → ')[1] || 'target'}
3. **Key Elements to Include**:
   - Visual representation of the code construct
   - Syntax differences highlighted
   - Data flow or execution model (if applicable)
   - Memory layout or structure (if relevant)
4. **Style**: ${visualStyle === 'clean' ? 'Clean, minimalist, professional' : 'Detailed with comprehensive annotations'}
5. **Labels**: Clear text labels in English
6. **Color Coding**: ${colorScheme === 'language-coded' ? 'Use colors to distinguish source vs target language elements' : 'Use neutral color scheme'}

**Diagram Type**: Focus on creating a ${diagramType} diagram

${customInstructions ? `**Additional Instructions:**\n${customInstructions}` : ''}

Generate a diagram that a developer learning ${languagePair.split(' → ')[1] || 'the target language'} from ${languagePair.split(' → ')[0] || 'the source language'} would find immediately helpful.`
  }

  const handleGenerate = async () => {
    setIsGenerating(true)
    try {
      const prompt = buildPrompt()
      const request: GenerationRequest = {
        languagePair,
        codeElement,
        concept: concept.split(':')[0] || 'Concept',
        context: { surface, concept, paradigm },
        prompt
      }

      const result = await imageLabService.generateImage(request)

      const newResult: GenerationResult = {
        id: Date.now().toString(),
        imageUrl: result.imageUrl,
        timestamp: new Date().toISOString(),
        responseTime: result.responseTime,
        fileSize: result.fileSize,
        model: result.model,
        prompt
      }

      setCurrentResult(newResult)
      setGenerationHistory(prev => [newResult, ...prev.slice(0, 9)]) // Keep last 10
    } catch (error) {
      console.error('Generation failed:', error)
      alert('Failed to generate image: ' + (error as Error).message)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleSaveTemplate = () => {
    const name = prompt('Template name:')
    if (!name) return

    const newTemplate: PromptTemplate = {
      id: Date.now().toString(),
      name,
      diagramType,
      visualStyle,
      colorScheme,
      customInstructions
    }

    const updated = [...templates, newTemplate]
    setTemplates(updated)
    localStorage.setItem('imagelab_templates', JSON.stringify(updated))
  }

  const handleLoadTemplate = (template: PromptTemplate) => {
    setDiagramType(template.diagramType)
    setVisualStyle(template.visualStyle)
    setColorScheme(template.colorScheme)
    setCustomInstructions(template.customInstructions)
  }

  const handleRating = (rating: 'A' | 'B' | 'C' | 'F') => {
    if (!currentResult) return
    const updated = { ...currentResult, rating }
    setCurrentResult(updated)
    setGenerationHistory(prev =>
      prev.map(r => r.id === updated.id ? updated : r)
    )
  }

  const fullPrompt = buildPrompt()

  return (
    <div className="min-h-screen bg-warm-bg">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-screen-2xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                <Image className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-slate-900">Image Prompt Lab</h1>
                <p className="text-sm text-slate-500">Experiment with AI diagram generation for SyntaxLens insights</p>
              </div>
            </div>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="imagelab-button-close"
              aria-label="Close Image Lab and return to previous page"
            >
              Close
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-screen-2xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT PANEL: Context & Prompt Builder */}
          <div className="space-y-6">
            {/* Load from Log */}
            <section className="bg-white rounded-lg border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-orange-500" />
                Load from Log
              </h2>
              <select
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                onChange={(e) => {
                  const log = logs.find(l => l.id === e.target.value)
                  if (log) handleLoadLog(log)
                }}
                value={selectedLog?.id || ''}
                data-testid="imagelab-select-log"
                aria-label="Select recent image generation log to load"
              >
                <option value="">Select a recent log...</option>
                {logs.map(log => (
                  <option key={log.id} value={log.id}>
                    {log.timestamp} - {log.languagePair} - {log.concept}
                  </option>
                ))}
              </select>
            </section>

            {/* Load from Examples */}
            <section className="bg-white rounded-lg border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-blue-500" />
                Load Example Context
              </h2>
              <select
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) => {
                  const defaultCtx = defaultContexts.find(c => c.id === e.target.value)
                  if (defaultCtx) handleLoadDefaultContext(defaultCtx)
                }}
                defaultValue=""
                data-testid="imagelab-select-example"
                aria-label="Select example context to load"
              >
                <option value="">Select an example...</option>
                {defaultContexts.map(ctx => (
                  <option key={ctx.id} value={ctx.id}>
                    {ctx.name}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-xs text-slate-500">
                Pre-configured contexts for common programming concepts
              </p>
            </section>

            {/* Context Preview */}
            <section className="bg-white rounded-lg border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Context Preview</h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Language Pair</label>
                  <input
                    type="text"
                    value={languagePair}
                    onChange={(e) => setLanguagePair(e.target.value)}
                    placeholder="C → Rust"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid="imagelab-input-languagepair"
                    aria-label="Enter language pair for comparison"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Code Element</label>
                  <input
                    type="text"
                    value={codeElement}
                    onChange={(e) => setCodeElement(e.target.value)}
                    placeholder="match"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid="imagelab-input-element"
                    aria-label="Enter code element being explained"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Surface (Syntax)</label>
                  <textarea
                    value={surface}
                    onChange={(e) => setSurface(e.target.value)}
                    placeholder="C: switch statement → Rust: match expression"
                    rows={2}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid="imagelab-textarea-surface"
                    aria-label="Enter surface level syntax comparison"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Concept (Semantic)</label>
                  <textarea
                    value={concept}
                    onChange={(e) => setConcept(e.target.value)}
                    placeholder="Semantic meaning and differences..."
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid="imagelab-textarea-concept"
                    aria-label="Enter concept level semantic explanation"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Paradigm (Philosophy)</label>
                  <textarea
                    value={paradigm}
                    onChange={(e) => setParadigm(e.target.value)}
                    placeholder="Design philosophy and paradigm differences..."
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid="imagelab-textarea-paradigm"
                    aria-label="Enter paradigm level design philosophy"
                  />
                </div>
              </div>
            </section>

            {/* Prompt Template Editor */}
            <section className="bg-white rounded-lg border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Prompt Template Editor</h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Diagram Type</label>
                  <select
                    value={diagramType}
                    onChange={(e) => setDiagramType(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid="imagelab-select-diagramtype"
                    aria-label="Select diagram type for generation"
                  >
                    <option value="control flow">Control Flow Diagram</option>
                    <option value="memory model">Memory Model</option>
                    <option value="syntax comparison">Syntax Comparison Chart</option>
                    <option value="data structure">Data Structure Visualization</option>
                    <option value="type system">Type System Comparison</option>
                    <option value="execution timeline">Execution Timeline</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Visual Style</label>
                  <select
                    value={visualStyle}
                    onChange={(e) => setVisualStyle(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid="imagelab-select-visualstyle"
                    aria-label="Select visual style for diagram"
                  >
                    <option value="clean">Clean & Minimalist</option>
                    <option value="detailed">Detailed & Annotated</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Color Scheme</label>
                  <select
                    value={colorScheme}
                    onChange={(e) => setColorScheme(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid="imagelab-select-colorscheme"
                    aria-label="Select color scheme for diagram"
                  >
                    <option value="language-coded">Language-Coded (Recommended)</option>
                    <option value="neutral">Neutral Palette</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Custom Instructions</label>
                  <textarea
                    value={customInstructions}
                    onChange={(e) => setCustomInstructions(e.target.value)}
                    placeholder="Add specific requirements or constraints..."
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid="imagelab-textarea-custominstructions"
                    aria-label="Add custom instructions for diagram generation"
                  />
                </div>

                {/* Template Management */}
                {templates.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Load Template</label>
                    <select
                      onChange={(e) => {
                        const template = templates.find(t => t.id === e.target.value)
                        if (template) handleLoadTemplate(template)
                      }}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      defaultValue=""
                      data-testid="imagelab-select-template"
                      aria-label="Load saved prompt template"
                    >
                      <option value="">Select a saved template...</option>
                      {templates.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </section>

            {/* Full Prompt Preview */}
            <section className="bg-white rounded-lg border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Full Prompt Preview</h2>
              <div className="bg-slate-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                <pre className="text-xs text-slate-700 whitespace-pre-wrap font-mono">{fullPrompt}</pre>
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating || !languagePair || !codeElement}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  data-testid="imagelab-button-generate"
                  data-state={isGenerating ? 'loading' : 'idle'}
                  aria-label="Generate diagram image with current prompt"
                >
                  <Sparkles className="w-4 h-4" />
                  {isGenerating ? 'Generating...' : 'Generate Image'}
                </button>
                <button
                  onClick={handleSaveTemplate}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
                  data-testid="imagelab-button-savetemplate"
                  aria-label="Save current prompt settings as template"
                >
                  <Plus className="w-4 h-4" />
                  Save Template
                </button>
              </div>
            </section>
          </div>

          {/* RIGHT PANEL: Results & Comparison */}
          <div className="space-y-6">
            {/* Current Generation */}
            <section className="bg-white rounded-lg border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Current Generation</h2>

              {currentResult ? (
                <div className="space-y-4">
                  {/* Image Preview */}
                  <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden border border-slate-200">
                    <img
                      src={currentResult.imageUrl}
                      alt="Generated diagram"
                      className="w-full h-full object-contain"
                      data-testid="imagelab-image-preview"
                    />
                  </div>

                  {/* Metrics */}
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Clock className="w-4 h-4" />
                      <span>{(currentResult.responseTime / 1000).toFixed(1)}s</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <HardDrive className="w-4 h-4" />
                      <span>{(currentResult.fileSize / 1024).toFixed(0)} KB</span>
                    </div>
                    <div className="text-slate-600">
                      <span className="font-mono text-xs">{currentResult.model}</span>
                    </div>
                  </div>

                  {/* Quick Rating */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Quick Rating</label>
                    <div className="flex gap-2">
                      {(['A', 'B', 'C', 'F'] as const).map(rating => (
                        <button
                          key={rating}
                          onClick={() => handleRating(rating)}
                          className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                            currentResult.rating === rating
                              ? 'bg-orange-500 text-white border-orange-500'
                              : 'bg-white text-slate-700 border-slate-300 hover:border-orange-300'
                          }`}
                          data-testid={`imagelab-button-rating-${rating.toLowerCase()}`}
                          aria-label={`Rate diagram as ${rating}`}
                        >
                          {rating}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Quality Checklist */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Quality Checklist</label>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries({
                        readability: 'Readability',
                        visualHierarchy: 'Visual Hierarchy',
                        colorCoding: 'Color Coding',
                        layout: 'Layout',
                        completeness: 'Completeness',
                        selfExplanatory: 'Self-Explanatory'
                      }).map(([key, label]) => (
                        <label key={key} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={selectedQuality[key as keyof typeof selectedQuality]}
                            onChange={(e) =>
                              setSelectedQuality(prev => ({ ...prev, [key]: e.target.checked }))
                            }
                            className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                            data-testid={`imagelab-checkbox-${key.toLowerCase()}`}
                            aria-label={`Mark ${label} as satisfactory`}
                          />
                          <span className="text-slate-700">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const a = document.createElement('a')
                        a.href = currentResult.imageUrl
                        a.download = `diagram-${currentResult.timestamp}.jpeg`
                        a.click()
                      }}
                      className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
                      data-testid="imagelab-button-download"
                      aria-label="Download generated diagram image"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                    <button
                      className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
                      data-testid="imagelab-button-share"
                      aria-label="Share diagram image"
                    >
                      <Share2 className="w-4 h-4" />
                      Share
                    </button>
                  </div>
                </div>
              ) : (
                <div className="aspect-video bg-slate-50 rounded-lg flex flex-col items-center justify-center text-slate-400 border-2 border-dashed border-slate-200">
                  <Image className="w-12 h-12 mb-2" />
                  <p className="text-sm">No generation yet</p>
                  <p className="text-xs mt-1">Click "Generate Image" to start</p>
                </div>
              )}
            </section>

            {/* History */}
            {generationHistory.length > 0 && (
              <section className="bg-white rounded-lg border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">History</h2>
                <div className="grid grid-cols-4 gap-3">
                  {generationHistory.map(result => (
                    <button
                      key={result.id}
                      onClick={() => setCurrentResult(result)}
                      className="relative group"
                      data-testid={`imagelab-thumbnail-${result.id}`}
                      aria-label={`View generation from ${new Date(result.timestamp).toLocaleTimeString()}`}
                    >
                      <img
                        src={result.imageUrl}
                        alt="History thumbnail"
                        className="w-full aspect-video object-cover rounded-lg border border-slate-200 group-hover:border-orange-300 transition-colors"
                      />
                      {result.rating && (
                        <div className={`absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          result.rating === 'A' ? 'bg-green-500' :
                          result.rating === 'B' ? 'bg-blue-500' :
                          result.rating === 'C' ? 'bg-yellow-500' :
                          'bg-red-500'
                        } text-white`}>
                          {result.rating}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
